import { Link } from 'react-router-dom'

const guide = {
  slug: 'porn-blocker-for-adults',
  category: 'holds',
  kicker: 'For yourself',
  title: 'A porn blocker for adults: why parental controls don\'t work when you hold the key',
  seoTitle: 'Porn Blocker for Adults (For Yourself, Not a Child)',
  description: `Most blockers are parental controls in disguise: someone else holds the passcode. What a porn blocker for yourself has to do differently, and what to check first.`,
  published: '2026-09-03',
  minutes: 7,
  intro: `Search for a porn blocker and most of what you find is built for a
          parent locking a child's phone. That design has one assumption baked
          in: the person setting the block and the person using the device are
          not the same. When you're blocking porn for yourself, that assumption
          is false, and everything built on it stops working. This guide
          explains what a blocker for adults has to do differently, and how to
          tell before you install one.`,
  next: ['porn-blocker-you-cant-uninstall', 'porn-blocker-without-accountability-partner', 'how-to-quit-porn'],
  body: (
    <>
      <h2>The parental-control assumption</h2>
      <p>
        A parental control works like this: a parent sets restrictions, a
        parent holds the passcode, a parent gets the report. The child can't
        undo any of it because the child doesn't hold the key. It's a good
        design for its purpose.
      </p>
      <p>
        Point the same tool at yourself and every part breaks. You set the
        restrictions, so you know what they are. You hold the passcode, or
        you can reset it with your own Apple ID. You get the report, which
        is a report to no one. The blocker holds exactly as long as you
        feel like being blocked, and it stops holding at the precise hour
        you don't. That's not a character flaw. It's a tool used against its
        design.
      </p>

      <h2>The three ways people patch it, and why they leak</h2>
      <ul>
        <li><b>A partner or friend holds the passcode.</b> Now the most private fight you have involves a witness, and on iPhone the Screen Time passcode is still resettable with the Apple ID on the phone, which is yours. Details in <Link to="/guides/lock-screen-time-so-it-cant-be-bypassed">locking Screen Time</Link>.</li>
        <li><b>An accountability app.</b> Someone receives your activity. For some people it works. For many it adds shame to the loop and turns the blocker into a surveillance system they end up removing. See <Link to="/guides/porn-blocker-without-accountability-partner">a blocker without an accountability partner</Link>.</li>
        <li><b>A "password you don't know".</b> A random code you don't memorise, an "uninstall protection" screen. On your own device, the recovery path (Apple ID, admin account, reinstall) is still yours.</li>
      </ul>

      <h2>What a blocker for adults has to do instead</h2>
      <p>Four properties. If one is missing, the block is a note on the door.</p>
      <ol>
        <li><b>Work below the browser.</b> Filtering by category at DNS level, so every app, every browser and private mode are covered without a list you maintain. How that works: <Link to="/guides/dns-porn-blocker">DNS porn blockers explained</Link>.</li>
        <li><b>Be attached to something you can't undo on impulse.</b> Not a passcode; a mechanism of the operating system. On iPhone and iPad that mechanism is <Link to="/guides/supervised-iphone-explained">supervision</Link>: a profile that cannot be removed and that forbids adding a VPN.</li>
        <li><b>Only tighten fast.</b> Adding a locked day, a curfew or a category takes effect at once. Loosening waits a delay long enough for an urge to pass. Nobody has to say no to you; the calendar does.</li>
        <li><b>Keep nothing about you.</b> No history, no screenshots, no report. The block restricts; it doesn't look. An adult doesn't need a witness, they need a door.</li>
      </ol>

      <h2>Questions to ask before you pay for any blocker</h2>
      <table>
        <thead>
          <tr><th>Ask</th><th>Parental-control answer</th><th>Adult-blocker answer</th></tr>
        </thead>
        <tbody>
          <tr><td>If I crack at 2am, can I turn it off in a minute?</td><td>Yes, you hold the passcode</td><td>No — loosening waits a delay</td></tr>
          <tr><td>Does it cover every app and private browsing?</td><td>Often browser-only</td><td>Yes, DNS level</td></tr>
          <tr><td>Does a VPN get around it?</td><td>Yes</td><td>Not on a supervised iPhone</td></tr>
          <tr><td>Who sees what I do?</td><td>The parent / the partner</td><td>Nobody — category filter, no history</td></tr>
          <tr><td>Can I make it stricter today?</td><td>Yes</td><td>Yes, immediately</td></tr>
          <tr><td>Can I make it looser tonight?</td><td>Yes</td><td>Tomorrow, after the delay</td></tr>
        </tbody>
      </table>

      <h2>How SELFCNTRL answers them</h2>
      <p>
        SELFCNTRL is built for the person who is both the one setting the
        block and the one it's meant to hold. On iPhone and iPad, a
        supervised profile filters pornography by category for every app,
        forbids VPNs, and can't be removed from the device. On Mac and
        Windows, the filter installs as an app tied to your account, with
        safe search enforced and night curfews. Everywhere, tightening is
        immediate and loosening waits twenty-four hours, with a floor that
        never drops. Nothing is read or stored; no one receives a report.
        And because every blocker says it can't be beaten, we put $100 on
        ours: strip the filter off a device we locked and we pay you, on top
        of a refund.
      </p>
      <p>
        Setup takes ten minutes, once, and starts with the device that's
        with you at night: <Link to="/guides/how-to-block-porn-on-iphone">the iPhone guide</Link>.
        If you're still deciding whether a lock is the right approach at
        all, start with <Link to="/guides/how-to-quit-porn">how to quit porn when willpower keeps losing</Link>.
      </p>
    </>
  ),
  faq: [
    {
      q: 'Is there a porn blocker I can use on myself, not a child?',
      a: `Yes, but the design has to be different: it can't rely on a
          passcode you hold. On iPhone and iPad that means a supervised
          profile that can't be removed; everywhere, a delay to loosen
          rather than a password. SELFCNTRL is built that way.`
    },
    {
      q: 'Do I need an accountability partner?',
      a: `No. A lock doesn't need a witness. If you want someone in the
          loop, you can tell them; nothing in the system requires it, and
          nothing is reported to anyone.`
    },
    {
      q: 'Can I still use the internet normally?',
      a: `Yes. Filtering is by category, so the web you actually use is
          untouched. Safe search is enforced, adult categories don't
          resolve, and you can block more (dating, gambling, specific
          sites) from your account if you want.`
    },
    {
      q: 'What if I genuinely want to stop using it?',
      a: `You can. Release goes through your account and takes effect after
          the cooling-off delay. Slow on purpose, never impossible. The
          delay exists so that the decision is made by the person you are
          in the afternoon, not at two in the morning.`
    }
  ]
}

export default guide
